import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { type FormEvent, useId, useState } from 'react';

import { Btn } from '../../components/Btn';
import { Field } from '../../components/Field';
import { Input } from '../../components/Input';
import { Modal } from '../../components/Modal';
import { type ColumnLayout, DataTable } from '../../components/Table';
import { listCategories, listProducts } from '../../data/fetchers';
import { upsertCategory } from '../../data/mutators';
import { canEditMasterData } from '../../lib/permissions';
import { useSession } from '../../stores/session';
import { ProductHeader } from './ProductHeader';

interface CatRow {
  id: string;
  name: string;
  skus: number;
}
export function CategoriesView() {
  const qc = useQueryClient(),
    formId = useId();
  const user = useSession((s) => s.user),
    canEdit = !!user && canEditMasterData(user);
  const categoriesQuery = useQuery({
    queryKey: ['categories'],
    queryFn: listCategories,
  });
  const productsQuery = useQuery({
    queryKey: ['products'],
    queryFn: listProducts,
  });
  const categories = categoriesQuery.data ?? [],
    products = productsQuery.data ?? [];
  const [search, setSearch] = useState('');
  const [edit, setEdit] = useState<{
    id: string;
    name: string;
    isNew?: boolean;
  } | null>(null);
  const [validation, setValidation] = useState('');
  const [notice, setNotice] = useState('');
  const mutation = useMutation({
    mutationFn: upsertCategory,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['categories'] });
      void qc.invalidateQueries({ queryKey: ['products-page'] });
      setEdit(null);
      setNotice('บันทึกหมวดหมู่แล้ว');
    },
  });
  const open = (category: { id: string; name: string; isNew?: boolean }) => {
    mutation.reset();
    setValidation('');
    setNotice('');
    setEdit(category);
  };
  const close = () => {
    if (!mutation.isPending) setEdit(null);
  };
  const save = (e: FormEvent) => {
    e.preventDefault();
    if (!canEdit || !edit || mutation.isPending) return;
    const id = edit.id.trim(),
      name = edit.name.trim();
    if (!id || !name) {
      setValidation('กรุณากรอกรหัสและชื่อหมวดหมู่');
      return;
    }
    if (edit.isNew && categories.some((c) => c.id === id)) {
      setValidation('รหัสหมวดหมู่นี้มีอยู่แล้ว กรุณาใช้รหัสอื่น');
      return;
    }
    setValidation('');
    mutation.mutate({ id, name });
  };
  const rows = categories
    .filter((c) =>
      (c.name + ' ' + c.id).toLowerCase().includes(search.trim().toLowerCase()),
    )
    .map((c) => ({
      ...c,
      skus: products.filter((p) => p.categoryId === c.id).length,
    }));
  const columns: ColumnDef<CatRow, unknown>[] = [
    {
      accessorKey: 'name',
      header: 'หมวดหมู่',
      meta: { className: 'bo-table-card-title' } satisfies ColumnLayout,
      cell: (c) =>
        canEdit ? (
          <button
            type="button"
            className="min-h-11 text-left font-semibold hover:text-primary"
            onClick={() => open(c.row.original)}
          >
            {c.row.original.name}
          </button>
        ) : (
          c.row.original.name
        ),
    },
    {
      accessorKey: 'id',
      header: 'รหัสหมวดหมู่',
      cell: (c) => (
        <span className="font-mono text-sm-label text-ink-3">
          {c.row.original.id}
        </span>
      ),
    },
    {
      accessorKey: 'skus',
      header: 'จำนวนสินค้า',
      meta: { align: 'right' } satisfies ColumnLayout,
      cell: (c) => <span className="num">{c.row.original.skus} รายการ</span>,
    },
    ...(canEdit
      ? [
          {
            id: 'actions',
            header: 'การจัดการ',
            meta: { align: 'right', className: 'w-px whitespace-nowrap' },
            cell: ({ row }: { row: { original: CatRow } }) => (
              <Btn size="sm" onClick={() => open(row.original)}>
                แก้ไข
              </Btn>
            ),
          },
        ]
      : []),
  ];
  const pending = categoriesQuery.isPending || productsQuery.isPending;
  const error = categoriesQuery.error || productsQuery.error;
  return (
    <div className="bo-fade">
      <ProductHeader
        title="หมวดหมู่สินค้า"
        subtitle="จัดกลุ่มสินค้าให้ค้นหาและดูแลรายการในหน้าร้านได้ง่ายขึ้น"
        actions={
          canEdit ? (
            <Btn
              kind="primary"
              disabled={pending || !!error}
              onClick={() => open({ id: '', name: '', isNew: true })}
            >
              + เพิ่มหมวดหมู่
            </Btn>
          ) : undefined
        }
      />
      <div className="bo-card mb-4 p-4">
        <Field label="ค้นหาหมวดหมู่">
          <Input
            aria-label="ค้นหาหมวดหมู่"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="ค้นหาด้วยชื่อหรือรหัสหมวดหมู่"
          />
        </Field>
      </div>
      <div className="mb-3 flex flex-wrap justify-between gap-3 text-sm-label">
        <span className="font-semibold">
          {pending ? 'กำลังโหลดหมวดหมู่' : rows.length + ' หมวดหมู่'}
        </span>
        <span className="text-ink-3">จำนวนสินค้านับรวมรายการที่ปิดใช้งาน</span>
      </div>
      {notice && (
        <p role="status" className="mb-3 text-ok">
          {notice}
        </p>
      )}
      {error ? (
        <div role="alert" className="bo-card p-5">
          โหลดหมวดหมู่ไม่สำเร็จ{' '}
          <Btn
            onClick={() => {
              void categoriesQuery.refetch();
              void productsQuery.refetch();
            }}
          >
            ลองใหม่
          </Btn>
        </div>
      ) : pending ? (
        <div
          role="status"
          className="h-32 animate-pulse rounded-card bg-line-2"
        >
          กำลังโหลดหมวดหมู่
        </div>
      ) : (
        <DataTable columns={columns} data={rows} mobileCards />
      )}
      <Modal
        open={!!edit}
        variant="drawer"
        className="[&>div:last-child]:bg-surface [&>div:last-child]:py-3"
        title={edit?.isNew ? 'เพิ่มหมวดหมู่' : 'แก้ไขหมวดหมู่'}
        description="ใช้หมวดหมู่เดียวกันกับสินค้าในทุกสาขา"
        onClose={close}
        footer={
          <div className="flex items-center justify-between gap-4">
            <p
              role="status"
              className="hidden min-w-0 items-center gap-2 text-sm-label text-ink-2 sm:flex"
            >
              <span
                aria-hidden="true"
                className={`h-2 w-2 shrink-0 rounded-full ${mutation.error || validation ? 'bg-danger' : mutation.isPending ? 'bg-warn' : 'bg-ok'}`}
              />
              <span>
                {mutation.isPending
                  ? 'กำลังบันทึก...'
                  : mutation.error || validation
                    ? 'บันทึกไม่สำเร็จ'
                    : edit?.isNew
                      ? 'เพิ่มหมวดหมู่ใหม่'
                      : 'แก้ไขหมวดหมู่'}
              </span>
            </p>
            <div className="grid w-full grid-cols-2 gap-2 sm:flex sm:w-auto">
              <Btn
                disabled={mutation.isPending}
                onClick={close}
                className="sm:min-w-24"
              >
                ยกเลิก
              </Btn>
              <Btn
                kind="primary"
                type="submit"
                form={formId}
                disabled={!canEdit || mutation.isPending}
                className="sm:min-w-32"
              >
                บันทึกหมวดหมู่
              </Btn>
            </div>
          </div>
        }
      >
        {edit && (
          <form id={formId} onSubmit={save} className="space-y-5">
            <Field label="ชื่อหมวดหมู่">
              <Input
                required
                aria-label="ชื่อหมวดหมู่"
                disabled={mutation.isPending}
                value={edit.name}
                onChange={(e) => setEdit({ ...edit, name: e.target.value })}
              />
            </Field>
            <Field label="รหัสหมวดหมู่">
              <Input
                required
                aria-label="รหัสหมวดหมู่"
                readOnly={!edit.isNew}
                disabled={mutation.isPending}
                value={edit.id}
                onChange={(e) => setEdit({ ...edit, id: e.target.value })}
              />
              <span className="mt-2 text-sm-label text-ink-3">
                {edit.isNew
                  ? 'ใช้รหัสที่ไม่ซ้ำกับหมวดหมู่เดิม'
                  : 'รหัสใช้อ้างอิงสินค้า จึงแก้ไขเฉพาะชื่อหมวดหมู่'}
              </span>
            </Field>
            {!edit.isNew && (
              <div className="rounded-card bg-bg-warm p-4">
                <p className="font-semibold">สินค้าที่อยู่ในหมวดหมู่นี้</p>
                <p className="mt-1">
                  {products.filter((p) => p.categoryId === edit.id).length}{' '}
                  รายการ
                </p>
                <p className="mt-3 text-sm-label text-ink-3">
                  การเปลี่ยนชื่อไม่ย้ายสินค้าออกจากหมวดหมู่
                </p>
              </div>
            )}
            {(validation || mutation.error) && (
              <p role="alert" className="text-danger">
                {validation ||
                  (mutation.error instanceof Error
                    ? mutation.error.message
                    : 'บันทึกหมวดหมู่ไม่สำเร็จ')}
              </p>
            )}
          </form>
        )}
      </Modal>
    </div>
  );
}
